import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { COOKIE_CONSENT_EVENT, DEFAULT_CATEGORIES } from '../constants';
import { buildConsent, needsReconsent, readConsent, writeConsent } from '../storage';
import { CookieCategory, type CookieCategoryMap, type CookieConsent } from '../types';

export interface CookieConsentContextValue {
  consent: CookieConsent | null;
  /** L'utente ha una scelta valida (non scaduta, versione corrente). */
  hasDecided: boolean;
  isCategoryGranted: (category: CookieCategory) => boolean;

  acceptAll: () => void;
  rejectAll: () => void;
  /** Salva una selezione parziale fondendola sullo stato corrente/default. */
  savePreferences: (partial: Partial<CookieCategoryMap>) => void;

  // Orchestrazione UI
  showBanner: boolean;
  isPreferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
}

export const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function emitChange(consent: CookieConsent): void {
  try {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: consent }));
  } catch {
    /* ambiente senza CustomEvent: ignora */
  }
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // Init sincrono dal localStorage → niente flash, decisione nota al primo paint.
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const [isPreferencesOpen, setPreferencesOpen] = useState(false);

  const persist = useCallback((categories: CookieCategoryMap) => {
    const record = buildConsent(categories);
    writeConsent(record);
    setConsent(record);
    setPreferencesOpen(false);
    emitChange(record);
    // Phase 2: se autenticato, sync fire-and-forget verso il backend.
  }, []);

  const acceptAll = useCallback(() => {
    persist({
      [CookieCategory.NECESSARY]: true,
      [CookieCategory.ANALYTICS]: true,
      [CookieCategory.PROFILING]: true,
    });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({
      [CookieCategory.NECESSARY]: true,
      [CookieCategory.ANALYTICS]: false,
      [CookieCategory.PROFILING]: false,
    });
  }, [persist]);

  const savePreferences = useCallback(
    (partial: Partial<CookieCategoryMap>) => {
      const base = consent?.categories ?? DEFAULT_CATEGORIES;
      persist({ ...base, ...partial });
    },
    [consent, persist]
  );

  const isCategoryGranted = useCallback(
    (category: CookieCategory) => {
      if (category === CookieCategory.NECESSARY) return true;
      return Boolean(consent?.categories?.[category]);
    },
    [consent]
  );

  const hasDecided = !needsReconsent(consent);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      hasDecided,
      isCategoryGranted,
      acceptAll,
      rejectAll,
      savePreferences,
      showBanner: !hasDecided,
      isPreferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
    }),
    [consent, hasDecided, isCategoryGranted, acceptAll, rejectAll, savePreferences, isPreferencesOpen]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}
